import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography
} from '@mui/material';
import { Refresh as RefreshIcon, Check as CheckIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import KPIStrip from './KPIStrip';
import PriorityQueue from './PriorityQueue';
import PortfolioHealth from './PortfolioHealth';
import WeeklyWinners from './WeeklyWinners';

const AgencyOverview = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [completedActionIds, setCompletedActionIds] = useState(new Set());
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    action: null
  });
  const [weeklyWinners, setWeeklyWinners] = useState([]);
  const [weeklyWinnersLoading, setWeeklyWinnersLoading] = useState(false);
  const [stats, setStats] = useState({
    totalClients: 0,
    clientsWithKeywords: 0,
    clientsWithPosts: 0,
    actionRequired: 0,
    portfolioHealth: { good: 0, warning: 0, critical: 0 },
    topPerformers: [],
    allProfiles: [],
    topPerformersByMetric: {
      impressions: [],
      anrufe: [],
      websiteClicks: []
    }
  });

  // Handle action item completion
  const handleActionCheckbox = (e, item) => {
    e.stopPropagation();
    e.preventDefault();
    setConfirmModal({
      open: true,
      action: item
    });
  };

  // Confirm and mark action as completed
  const handleConfirmCompletion = async (e) => {
    e?.stopPropagation();
    e?.preventDefault();

    if (confirmModal.action) {
      const item = confirmModal.action;

      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch('/api/agency/action-queue/complete', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            businessId: item.businessId,
            actionType: item.actionType
          })
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const uniqueActionId = `${item.businessId}_${item.actionType}`;
        const newCompleted = new Set(completedActionIds);
        newCompleted.add(uniqueActionId);
        setCompletedActionIds(newCompleted);

        setTimeout(() => {
          const updated = actionItems.filter(a =>
            `${a.businessId}_${a.actionType}` !== uniqueActionId
          );
          setActionItems(updated);
          setConfirmModal({ open: false, action: null });
          console.log('✅ Action completed successfully.');
        }, 300);
      } catch (error) {
        console.error('❌ Failed to complete action:', error);
        setConfirmModal({ open: false, action: null });
        alert('Fehler beim Aktualisieren der Aktion. Bitte versuchen Sie es später.');
      }
    }
  };

  const handleCancelCompletion = () => {
    setConfirmModal({ open: false, action: null });
  };

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        setError('Authentifizierung erforderlich');
        setLoading(false);
        return;
      }

      console.log('🔄 Starting to fetch overview data...');

      // Fetch cached profile data
      const cacheResponse = await fetch('/api/geschaeftsprofile-cache?limit=500', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!cacheResponse.ok) {
        throw new Error(`Cache HTTP ${cacheResponse.status}`);
      }

      const cacheData = await cacheResponse.json();

      // Fetch top performers data with timeout
      let topPerformersByMetric = { impressions: [], anrufe: [], websiteClicks: [] };
      try {
        console.log('📡 Fetching top performers data...');

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);

        const topPerfResponse = await fetch('/api/analytics/top-performers-before-after', {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (topPerfResponse.ok) {
          const reader = topPerfResponse.body?.getReader();
          if (reader) {
            const decoder = new TextDecoder();
            let buffer = '';
            const allBusinessData = [];
            let lineCount = 0;
            let dataCount = 0;

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                lineCount++;
                if (line.trim()) {
                  try {
                    const parsed = JSON.parse(line);
                    if (parsed.type === 'data' && parsed.business) {
                      dataCount++;
                      allBusinessData.push(parsed.business);
                    }
                  } catch (parseErr) {
                    console.warn('⚠️ Failed to parse line', lineCount);
                  }
                }
              }

              if (lineCount % 50 === 0) {
                console.log(`📊 Streamed ${dataCount} businesses so far...`);
              }
            }

            if (buffer.trim()) {
              try {
                const parsed = JSON.parse(buffer);
                if (parsed.type === 'data' && parsed.business) {
                  allBusinessData.push(parsed.business);
                  dataCount++;
                }
              } catch (e) {
                // Ignore
              }
            }

            console.log('✅ Parsed businesses from top performers:', dataCount);

            // Sort by metrics
            if (allBusinessData.length > 0) {
              const getMetricValue = (stats, metricKey) => {
                return stats?.[metricKey]?.total || 0;
              };

              const getTotalImpressions = (stats) => {
                if (!stats) return 0;
                return (getMetricValue(stats, 'BUSINESS_IMPRESSIONS_DESKTOP_SEARCH') +
                        getMetricValue(stats, 'BUSINESS_IMPRESSIONS_MOBILE_SEARCH') +
                        getMetricValue(stats, 'BUSINESS_IMPRESSIONS_DESKTOP_MAPS') +
                        getMetricValue(stats, 'BUSINESS_IMPRESSIONS_MOBILE_MAPS'));
              };

              const getPercentageChange = (before, after) => {
                if (!before || before === 0) return 0;
                return Math.round(((after - before) / before) * 100);
              };

              const sortByImpression = [...allBusinessData]
                .map(b => ({
                  ...b,
                  afterValue: getTotalImpressions(b.afterPeriodData?.summaryStats),
                  beforeValue: getTotalImpressions(b.beforePeriodData?.summaryStats)
                }))
                .map(b => ({
                  ...b,
                  percentageChange: getPercentageChange(b.beforeValue, b.afterValue)
                }))
                .filter(b => b.afterValue > 0)
                .sort((a, b) => b.percentageChange - a.percentageChange)
                .slice(0, 3);

              topPerformersByMetric = {
                impressions: sortByImpression,
                anrufe: [],
                websiteClicks: []
              };
            }
          }
        }
      } catch (e) {
        console.warn('⚠️ Could not fetch top performer data:', e.message);
      }

      if (cacheData.success && cacheData.data) {
        const allProfiles = cacheData.data;
        const totalClients = allProfiles.length;
        const clientsWithKeywords = allProfiles.filter(p => (p.keywordsCount || 0) > 0).length;
        const clientsWithPosts = allProfiles.filter(p => (p.postsCount || 0) > 0).length;

        const portfolioHealth = {
          good: allProfiles.filter(p => p.status === 'Aktiv').length,
          warning: allProfiles.filter(p => p.status === 'Warnung').length,
          critical: allProfiles.filter(p => p.status === 'Kritisch').length
        };

        const actionRequired = portfolioHealth.warning + portfolioHealth.critical;

        const topPerformers = [...allProfiles]
          .filter(p => p.averageRank !== null && p.averageRank !== undefined)
          .sort((a, b) => (a.averageRank || 100) - (b.averageRank || 100))
          .slice(0, 3);

        setStats({
          totalClients,
          clientsWithKeywords,
          clientsWithPosts,
          actionRequired,
          portfolioHealth,
          topPerformers,
          allProfiles,
          topPerformersByMetric
        });
      } else {
        setError(cacheData.error || 'Fehler beim Laden der Daten');
      }
    } catch (err) {
      console.error('Error loading overview data:', err);
      setError(err.message || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const fetchActionQueue = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      // Fetch with high limit to get all items from cache
      const response = await fetch('/api/agency/action-queue?limit=1000', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        let items = data.data || [];
        let completed = data.completed || [];

        // Combine all items for display (active + completed)
        const allItems = [...items, ...completed];
        setActionItems(allItems);

        console.log('✅ Action queue loaded:', {
          active: items.length,
          completed: completed.length,
          total: allItems.length,
          breakdown: data.breakdown
        });
      } else {
        console.error('❌ API error:', response.status);
      }
    } catch (err) {
      console.warn('⚠️ Failed to fetch action queue:', err.message);
    }
  };

  const fetchWeeklyWinners = async () => {
    try {
      setWeeklyWinnersLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/analytics/top-keywords-overview?months=3', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const topBusinesses = data.topBusinesses || [];
        setWeeklyWinners(topBusinesses);
        console.log('✅ Weekly winners loaded:', topBusinesses.length, 'businesses');
      } else {
        console.error('❌ Failed to fetch weekly winners:', response.status);
        setWeeklyWinners([]);
      }
    } catch (err) {
      console.warn('⚠️ Failed to fetch weekly winners:', err.message);
      setWeeklyWinners([]);
    } finally {
      setWeeklyWinnersLoading(false);
    }
  };

  useEffect(() => {
    fetchOverviewData();
    fetchActionQueue();
    fetchWeeklyWinners();
  }, []);

  // Transform action items for PriorityQueue component - include ALL items so tabs can filter
  const priorityQueueItems = actionItems.map((item) => {
    // Map category to status display
    let status = 'Offen';
    if (item.completedAt) {
      status = 'Abgeschlossen';
    } else if (item.category === 'critical') {
      status = 'Ranking gefallen';
    } else if (item.category === 'important') {
      // Determine specific important status based on actionType
      status = item.actionType?.includes('Setup') ? 'Einrichtung offen' : 'Bewertung offen';
    } else if (item.category === 'optimization') {
      status = 'Sichtbarkeit';
    }

    // Find the locationId from allProfiles using businessId
    const profile = stats.allProfiles.find(p => p.businessProfileId === item.businessId || p._id === item.businessId);
    const locationId = profile?.locationId || item.businessId;

    return {
      businessName: item.businessName || 'Unbenannt',
      status: status,
      detail: item.reason || item.actionType || 'Keine Details verfügbar',
      initials: item.businessName?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'XX',
      avatarColor: item.category === 'critical' ? '#E05068' : item.category === 'important' ? '#E0901B' : '#1E9E5A',
      businessId: item.businessId,
      locationId: locationId,
      actionType: item.actionType,
      category: item.category,
      completedAt: item.completedAt
    };
  });

  return (
    <Box sx={{ p: '30px 34px', backgroundColor: '#F7F6F3', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 700,
              color: '#C80F2E',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontFamily: 'Plus Jakarta Sans, system-ui',
              mb: 1
            }}
          >
            AGENTUR-COCKPIT
          </Typography>
          <Typography
            sx={{
              fontSize: '30px',
              fontWeight: 800,
              color: '#0A0A0B',
              fontFamily: 'Plus Jakarta Sans, system-ui',
              letterSpacing: '-0.025em',
              mb: 0.5
            }}
          >
            Guten Morgen, {user?.name || 'Benutzer'}.
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#55555D',
              fontFamily: 'Plus Jakarta Sans, system-ui'
            }}
          >
            Heute brauchen <strong>{actionItems.length} von {stats.totalClients}</strong> Unternehmen deine Aufmerksamkeit.
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography
            sx={{
              fontSize: '12px',
              color: '#55555D',
              fontFamily: 'Plus Jakarta Sans, system-ui',
              mb: 1
            }}
          >
            Aktualisiert vor 35 Min
          </Typography>
          <Tooltip title="Daten aktualisieren">
            <Button
              variant="contained"
              onClick={fetchOverviewData}
              disabled={loading}
              startIcon={<RefreshIcon />}
              sx={{
                backgroundColor: '#C80F2E',
                color: '#FFFFFF',
                fontSize: '12px',
                fontWeight: 600,
                padding: '8px 12px',
                borderRadius: '10px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#AE0B27',
                  boxShadow: '0 4px 14px rgba(200,15,46,.28)'
                }
              }}
            >
              Daten aktualisieren
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* KPI Strip */}
          <KPIStrip
            totalClients={stats.totalClients}
            clientsWithKeywords={stats.clientsWithKeywords}
            clientsWithPosts={stats.clientsWithPosts}
            actionRequired={stats.actionRequired}
          />

          {/* Two-Column Layout */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1.62fr 1fr',
              gap: '22px',
              '@media (max-width: 1024px)': {
                gridTemplateColumns: '1fr'
              }
            }}
          >
            {/* Left Column: Priority Queue */}
            <PriorityQueue
              items={priorityQueueItems}
              onViewAll={() => navigate('/all-business-profiles')}
              onItemAction={(item) => {
                const getRedirectPath = (item) => {
                  const actionType = item.actionType?.toLowerCase() || '';
                  const locationId = item.locationId;

                  // Keywords-related → ranking page
                  if (actionType.includes('keyword')) {
                    return `/ranking/${locationId}`;
                  }

                  // Media-related → posts page (media management)
                  if (actionType.includes('media')) {
                    return `/posts/${locationId}`;
                  }

                  // Posts/content-related → posts page
                  if (actionType.includes('post') || actionType.includes('scheduled')) {
                    return `/posts/${locationId}`;
                  }

                  // Services-related → services page
                  if (actionType.includes('service')) {
                    return `/profile/${locationId}`;
                  }

                  // Ranking-related → ranking page
                  if (actionType.includes('ranking')) {
                    return `/ranking/${locationId}`;
                  }

                  // Default → profile page
                  return `/profile/${locationId}`;
                };

                navigate(getRedirectPath(item));
              }}
              onItemCheckbox={handleActionCheckbox}
            />

            {/* Right Column: Portfolio Health + Weekly Winners */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
              <PortfolioHealth
                good={stats.portfolioHealth.good}
                warning={stats.portfolioHealth.warning}
                critical={stats.portfolioHealth.critical}
              />
              <WeeklyWinners
                topBusinesses={weeklyWinners}
                loading={weeklyWinnersLoading}
                onViewAnalysis={() => navigate('/keyword-performance')}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* Confirmation Modal */}
      <Dialog
        open={confirmModal.open}
        onClose={handleCancelCompletion}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#FFFFFF', color: '#0A0A0B', fontWeight: 700, borderBottom: '1px solid #ECEBEE' }}>
          ✓ Aktion abgeschlossen?
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#FFFFFF', py: 3 }}>
          {confirmModal.action && (
            <Box>
              <Typography sx={{ mb: 2, color: '#0A0A0B' }}>
                Bestätigen Sie, dass folgende Aktion erledigt ist:
              </Typography>
              <Box sx={{
                p: 2,
                backgroundColor: '#F8F7F9',
                borderRadius: 1,
                border: '1px solid #ECEBEE',
                mb: 2
              }}>
                <Typography sx={{ fontWeight: 600, color: '#0A0A0B', mb: 1 }}>
                  {confirmModal.action.businessName}
                </Typography>
                <Typography variant="body2" sx={{ color: '#55555D' }}>
                  {confirmModal.action.reason}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#55555D', fontStyle: 'italic' }}>
                Diese Aktion wird von der Liste entfernt.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#FFFFFF', p: 2, borderTop: '1px solid #ECEBEE' }}>
          <Button
            onClick={handleCancelCompletion}
            sx={{ color: '#55555D' }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmCompletion}
            variant="contained"
            sx={{
              backgroundColor: '#1E9E5A',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#178049' }
            }}
            startIcon={<CheckIcon />}
          >
            Ja, Erledigt!
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgencyOverview;
